/**
 * 大气散射共享 GLSL 代码块（atmosphere 主着色器与多次散射 LUT 着色器共用）。
 *
 * 使用方必须在着色器中声明如下 uniform（common 内的函数会引用）：
 *   uBetaR, uBetaM, uBetaMExt, uHr, uHm, uPlanetR, uAtmosR, uG,
 *   uSunIrradiance, uTransLUT, uSampleSteps, uRayleighOnly, uMieOnly, uNoScatter
 *
 * 提供：
 *  - 常量 PI / E
 *  - intersectSphere / atmosphereSegment：射线-球体相交与大气区间
 *  - lutUV：透射率 LUT 采样坐标
 *  - rayleighPhase / hgPhase / totalPhase：相位函数
 *  - computeSingleScatter：单散射辐亮度积分（含太阳辐照度），sunDir 作为参数传入
 */

export const atmosphereCommonChunk = /* glsl */ `
const float PI = 3.14159265358979323846;
const float E = 2.71828182845904523536;

// 射线-球体相交：返回 (t0, t1)；未命中或球在后方返回 (-1)
vec2 intersectSphere(vec3 ro, vec3 rd, float radius) {
  float b = dot(ro, rd);
  float c = dot(ro, ro) - radius * radius;
  float disc = b * b - c;
  if (disc < 0.0) return vec2(-1.0);
  float sq = sqrt(disc);
  float t0 = -b - sq;
  float t1 = -b + sq;
  if (t1 < 0.0) return vec2(-1.0);
  return vec2(max(t0, 0.0), t1);
}

// 相机出发的射线在大气壳层内的区间；返回 (-1) 表示未穿过
vec2 atmosphereSegment(vec3 ro, vec3 rd) {
  vec2 top = intersectSphere(ro, rd, uAtmosR);
  if (top.x < 0.0) return vec2(-1.0);
  float entry = max(top.x, 0.0);
  vec2 ground = intersectSphere(ro, rd, uPlanetR);
  float exit = top.y;
  if (ground.x > entry) exit = min(exit, ground.x);
  if (exit <= entry) return vec2(-1.0);
  return vec2(entry, exit);
}

// LUT 采样坐标：u=μ 映射 [0,1]；v=0 大气顶、v=1 地表
vec2 lutUV(float r, float mu) {
  float u = clamp(mu * 0.5 + 0.5, 0.0, 1.0);
  float v = clamp((uAtmosR - r) / (uAtmosR - uPlanetR), 0.0, 1.0);
  return vec2(u, v);
}

// Rayleigh 相位函数
float rayleighPhase(float mu) {
  return (3.0 / (16.0 * PI)) * (1.0 + mu * mu);
}

// Henyey-Greenstein 相位函数
float hgPhase(float mu, float g) {
  float g2 = g * g;
  return (1.0 - g2) / (4.0 * PI * pow(1.0 + g2 - 2.0 * g * mu, 1.5));
}

// 按散射系数亮度加权的 Rayleigh+Mie 混合相位（用于多次散射）
float totalPhase(float mu) {
  float wR = dot(uBetaR, vec3(0.33, 0.33, 0.34));
  float wM = dot(uBetaM, vec3(0.33, 0.33, 0.34));
  return (wR * rayleighPhase(mu) + wM * hgPhase(mu, uG)) / max(wR + wM, 1e-6);
}

// 沿 rd 方向从 ro 起的单散射辐亮度积分（含太阳辐照度）。
// tauView 输出视线方向光学厚度（用于地表合成衰减）。
// sunDir 作为参数传入：主着色器用 uSunDir，LUT 着色器用逐 texel 的太阳方向。
vec3 computeSingleScatter(vec3 ro, vec3 rd, vec3 sunDir, out vec3 tauView) {
  vec2 seg = atmosphereSegment(ro, rd);
  tauView = vec3(0.0);
  if (seg.x < 0.0) return vec3(0.0);
  float entry = seg.x;
  float exit = seg.y;
  float ds = (exit - entry) / float(uSampleSteps);
  vec3 inScatter = vec3(0.0);
  for (int i = 0; i < 128; i++) {
    if (i >= uSampleSteps) break;
    float t = entry + (float(i) + 0.5) * ds;
    vec3 P = ro + rd * t;
    float r = length(P);
    float h = max(r - uPlanetR, 0.0);
    float rhoR = exp(-h / uHr);
    float rhoM = exp(-h / uHm);
    vec3 extinction = uBetaR * rhoR + uBetaMExt * rhoM;
    tauView += extinction * ds;
    vec3 dirP = P / max(r, 1e-6);
    float muS = dot(dirP, sunDir);
    vec3 tauSun = texture2D(uTransLUT, lutUV(r, muS)).rgb;
    vec3 sunTrans = exp(-tauSun);
    float mu = dot(rd, sunDir);
    float phaseR = rayleighPhase(mu);
    float phaseM = hgPhase(mu, uG);
    vec3 scatter = vec3(0.0);
    if (uNoScatter == 0) {
      scatter += uBetaR * rhoR * phaseR * sunTrans;
      if (uRayleighOnly == 0) {
        scatter += uBetaM * rhoM * phaseM * sunTrans;
      }
    }
    if (uMieOnly == 1) {
      scatter = uBetaM * rhoM * phaseM * sunTrans;
    }
    inScatter += scatter * exp(-tauView) * ds;
  }
  return inScatter * uSunIrradiance;
}
`
